import React from "react";
import dynamic from "next/dynamic";

import { City } from "@/custom-types/CitiesNearMe";
import { CountryCode } from "@/custom-types/CountryCode";
import { CopyBlock as CopyBlockType, FAQ } from "@/custom-types/Directus";
import { DispensaryV3 } from "@/custom-types/DispensariesV3";
import { Location } from "@/custom-types/Location";
import { RetailType } from "@/custom-types/Store";

import CopyBlock from "@/components/CopyBlock/CopyBlock";
import ContentLayout from "@/components/dispensaries/ContentSection/ContentLayout";
import RecentlyViewedCarouselWithData from "@/components/dispensaries/RecentlyViewedCarousel";
import RetailCategoryNavigation from "@/components/dispensaries/RetailCategoryNavigation";
import StoreBreadcrumbs from "@/components/dispensaries/StoreBreadcrumbs";
import StoreMenuContainer from "@/components/dispensaries/StoreMenuContainer/StoreMenuContainer";
import FAQs from "@/components/FAQ";

import {
  STORE_LOCATOR_RESULTS_TAKE,
  StoreLocatorResultsResponse,
} from "../../../api/requests/consumerApi/getStoreLocatorResults";
import PageNavigation from "../../PageNavigation";
import CitiesNearby from "../CitiesNearby/CitiesNearby";
import Footer from "../Footer/Footer";

const CBDStoresCarousel = dynamic(
  () => import("../CBDStoresCarousel/CBDStoresCarousel"),
  { ssr: true },
);

interface ContentSectionProps {
  cbdStores: DispensaryV3[];
  cities: City[];
  copyBlockData: CopyBlockType | null;
  faqs: FAQ | null;
  isLocationPage: boolean;
  page: number;
  pageLocation: Location;
  retailType: RetailType;
  storeLocatorResults: StoreLocatorResultsResponse;
  showDeliveryValidation: boolean;
  countryCode: CountryCode;
  deliveryFilterSelected: boolean;
}

const ContentSection = ({
  cbdStores,
  cities,
  copyBlockData,
  faqs,
  deliveryFilterSelected,
  isLocationPage,
  page,
  pageLocation,
  retailType,
  showDeliveryValidation,
  storeLocatorResults,
  countryCode,
}: ContentSectionProps) => {
  const product = {
    "cbd-store": "hemp / CBD",
    clinic: "MMJ Doctors",
    dispensary: "weed",
    undefined: "weed",
  };

  const totalPages = Math.ceil(
    storeLocatorResults.metadata.totalCount / STORE_LOCATOR_RESULTS_TAKE,
  );

  return (
    <ContentLayout>
      <div className="flex flex-col h-full bg-white gap-md md:gap-0">
        <StoreMenuContainer
          storeLocatorResults={storeLocatorResults}
          retailType={retailType}
          pageLocation={pageLocation}
          showDeliveryValidation={showDeliveryValidation}
          showingCBDTreatment={cbdStores.length > 0}
          deliveryFilterSelected={deliveryFilterSelected}
        >
          <PageNavigation currentPage={page} totalPages={totalPages} />
          <RecentlyViewedCarouselWithData />
          {cbdStores.length === 0 ? null : (
            <CBDStoresCarousel
              cbdStores={cbdStores}
              pageLocation={pageLocation}
            />
          )}
          <RetailCategoryNavigation retailType={retailType} />
          <CitiesNearby
            cities={cities}
            heading={`Find ${
              product[retailType as keyof typeof product]
            } in a city near you`}
            retailType={retailType}
          />
          <hr className="border-light-grey" />
          {copyBlockData?.copy_block?.length ? (
            <CopyBlock data={copyBlockData} />
          ) : (
            <></>
          )}
          <FAQs
            data={faqs?.faqs}
            initialShowPanel={true}
            className="bg-green-20 flex flex-col gap-lg py-xxl [&_div]:mb-0 !m-0"
          />
          <StoreBreadcrumbs
            isLocationPage={isLocationPage}
            pageLocation={pageLocation}
            countryCode={countryCode}
            retailType={retailType}
          />
          <Footer className="!px-0" />
        </StoreMenuContainer>
      </div>
    </ContentLayout>
  );
};

export default ContentSection;
